import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupKpi — group KPI roll-up bar lifecycle hook (Ship 3).
//
// The deterministic, READ-ONLY analogue of useRollupBrief. Wires
// GroupKpiBar to the get-group-kpi-snapshot edge function. Unlike the
// roll-up brief there is NO cache table: the fn is a plain read, so we
// fetch directly and hold the result in component state.
//
//   1. On mount (and when groupId resolves), POST { group_id } to
//      get-group-kpi-snapshot. phase='loading' → 'ready' | 'error'.
//   2. regenerate() always refetches (Refresh button). The 5-minute
//      window only DEBOUNCES that button — we never auto-poll.
//   3. inFlightRef synchronous double-fire guard.
//
// The body is just { group_id }; the server's group-ownership gate is
// the tenant boundary.

const REFRESH_DEBOUNCE_MS = 5 * 60 * 1000

export function useGroupKpi(groupId) {
  // --- All hooks declared BEFORE any conditional return (React #310). ---
  const [phase, setPhase]       = useState('loading')  // loading | ready | error
  const [data, setData]         = useState(null)
  const [perUnit, setPerUnit]   = useState(null)
  const [fetchedAt, setFetched] = useState(null)

  // Synchronous in-flight guard — set before the first await, cleared
  // in finally. Prevents two quick Refresh clicks slipping through the
  // render cycle.
  const inFlightRef = useRef(false)
  // Last successful fetch instant (ms) — backs the Refresh debounce.
  const lastFetchMsRef = useRef(0)

  const fetchSnapshot = useCallback(async () => {
    if (!groupId) return
    if (inFlightRef.current) return

    inFlightRef.current = true
    setPhase((p) => (p === 'ready' ? p : 'loading'))
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/get-group-kpi-snapshot`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ group_id: groupId }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const payload = await resp.json()
      if (payload?.error) {
        setPhase('error')
        return
      }
      const { per_unit_breakdown, ...headline } = payload || {}
      setData(headline)
      setPerUnit(per_unit_breakdown || {})
      setFetched(new Date().toISOString())
      lastFetchMsRef.current = Date.now()
      setPhase('ready')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useGroupKpi] snapshot fetch failed', e)
      setPhase('error')
    } finally {
      inFlightRef.current = false
    }
  }, [groupId])

  // Initial load — kick off once groupId resolves.
  useEffect(() => {
    if (!groupId) return
    fetchSnapshot()
  }, [groupId, fetchSnapshot])

  // regenerate() always refetches, but the 5-minute window debounces
  // rapid Refresh taps so we don't hammer the read.
  const regenerate = useCallback(() => {
    if (inFlightRef.current) return
    if (Date.now() - lastFetchMsRef.current < REFRESH_DEBOUNCE_MS) return
    return fetchSnapshot()
  }, [fetchSnapshot])

  return useMemo(
    () => ({ phase, data, perUnit, fetchedAt, regenerate }),
    [phase, data, perUnit, fetchedAt, regenerate],
  )
}
